import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, Pencil, Trash2, Search as SearchIcon, X, Mail, MessageCircle, Globe, Phone, Upload, Download, CheckCircle2, AlertCircle, AlertTriangle, Send, Sparkles, ShieldCheck, Loader2 } from 'lucide-react';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import {
  listSuppliers, createSupplier, updateSupplier, deleteSupplier,
  supplierImportPreview, supplierImportCommit, exportSuppliersUrl,
  dangerResetSuppliers,
  listRfqCampaigns, createRfqCampaign, updateRfqCampaign, sendRfqCampaign,
  listRfqTemplates, previewRfq,
  enrichSupplier, bulkEnrichSuppliers, verifySupplier,
  verifySupplierEmail, bulkVerifySupplierEmails, markSupplierEmailVerified,
} from '../../lib/rfqApi';

const SUPPLIER_TYPES = [
  'Functional B/C',
  'Salvage / Refurbishment',
  'Both',
  'Review Before RFQ',
  'Do Not RFQ - Listed Inventory Only',
];
const STATUSES = ['new', 'contacted', 'quoted', 'approved', 'rejected', 'inactive'];
const ENRICHMENT_STATUSES = ['needs_enrichment', 'enriched', 'verified', 'invalid', 'no_contact_found'];
const EMAIL_VERIFICATION_STATUSES = ['unverified', 'valid_mx', 'risky', 'invalid', 'unknown', 'verified'];
const EMAIL_VERIFICATION_COLOR = {
  unverified: 'bg-amber-400/15 text-amber-200 border-amber-400/40',
  valid_mx:   'bg-cyan-400/15 text-cyan-200 border-cyan-400/40',
  verified:   'bg-emerald-400/15 text-emerald-200 border-emerald-400/40',
  risky:      'bg-orange-400/15 text-orange-200 border-orange-400/40',
  invalid:    'bg-red-400/15 text-red-200 border-red-400/40',
  unknown:    'bg-white/10 text-white/55 border-white/20',
};
const EMAIL_VERIFICATION_LABEL = {
  unverified: 'email unverified',
  valid_mx:   'MX OK',
  verified:   'manual-verified',
  risky:      'risky',
  invalid:    'invalid',
  unknown:    'unknown',
};
const needsEmailVerify = (s) =>
  !!(s.email || '').trim() && !['valid_mx', 'verified'].includes((s.email_verification_status || '').trim());
const ENRICHMENT_COLOR = {
  needs_enrichment: 'bg-amber-400/15 text-amber-200 border-amber-400/40',
  enriched: 'bg-cyan-400/15 text-cyan-200 border-cyan-400/40',
  verified: 'bg-emerald-400/15 text-emerald-200 border-emerald-400/40',
  invalid: 'bg-red-400/15 text-red-200 border-red-400/40',
  no_contact_found: 'bg-white/10 text-white/55 border-white/20',
};
const supplierNeedsEnrichment = (s) =>
  !((s.email || '').trim() && (s.website || '').trim() && (s.phone || '').trim() && (s.whatsapp || '').trim() && (s.contact_name || '').trim());
const BRAND_OPTIONS = ['HP', 'Dell', 'Lenovo', 'Apple', 'Other'];
const CATEGORY_OPTIONS = ['laptops', 'desktops', 'monitors', 'parts', 'accessories'];
const TAG_OPTIONS = [
  'UAE', 'LATAM', 'USA', 'Canada', 'Europe', 'Australia',
  'HP Specialist', 'Dell Specialist', 'Lenovo Specialist',
  'Laptops', 'Desktops', 'Grade A', 'Export Friendly', 'Contact First',
];

const STATUS_COLOR = {
  new: 'bg-white/10 text-white/70',
  contacted: 'bg-blue-400/15 text-blue-200',
  quoted: 'bg-teal-400/15 text-teal-200',
  approved: 'bg-emerald-400/15 text-emerald-200',
  rejected: 'bg-red-400/15 text-red-200',
  inactive: 'bg-white/5 text-white/40',
};

// ---------------------------------------------------------------------------
// Admin-side CRM helpers (frontend-only — no backend dependency).
// Derive ranking, inventory chips, last-contact date, and next action from
// existing supplier fields. If a real `ranking` / `next_action` is ever added
// to the schema, these helpers read it directly.
// ---------------------------------------------------------------------------
const RANKING_BY_STATUS = {
  preferred: { stars: 5, label: 'Preferred' },
  approved:  { stars: 4, label: 'Active' },
  active:    { stars: 4, label: 'Active' },
  qualified: { stars: 3, label: 'Qualified' },
  quoted:    { stars: 3, label: 'Qualified' },
  contacted: { stars: 2, label: 'Prospect' },
  prospect:  { stars: 2, label: 'Prospect' },
  new:       { stars: 1, label: 'Research/New' },
  research:  { stars: 1, label: 'Research/New' },
};
// Map text tier labels (CSV imports often ship these) to star counts.
const RANKING_BY_TIER_TEXT = {
  preferred: { stars: 5, label: 'Preferred' },
  approved:  { stars: 4, label: 'Active' },
  active:    { stars: 4, label: 'Active' },
  qualified: { stars: 3, label: 'Qualified' },
  contacted: { stars: 2, label: 'Prospect' },
  prospect:  { stars: 2, label: 'Prospect' },
  research:  { stars: 1, label: 'Research/New' },
  new:       { stars: 1, label: 'Research/New' },
};
const supplierRanking = (s) => {
  // Numeric ranking field wins — supports `ranking`, `supplier_rank`, `rank`.
  const numeric = [s?.ranking, s?.supplier_rank, s?.rank]
    .map((v) => Number(v))
    .find((n) => Number.isFinite(n) && n >= 1 && n <= 5);
  if (numeric) {
    const STAR_LABEL = { 5: 'Preferred', 4: 'Active', 3: 'Qualified', 2: 'Prospect', 1: 'Research/New' };
    return { stars: Math.round(numeric), label: STAR_LABEL[Math.round(numeric)] || 'Ranked' };
  }
  // Text tier label next — `supplier_tier`, or status.
  const tier = (s?.supplier_tier || '').toString().trim().toLowerCase();
  if (tier && RANKING_BY_TIER_TEXT[tier]) return RANKING_BY_TIER_TEXT[tier];
  return RANKING_BY_STATUS[(s?.status || '').toLowerCase()] || RANKING_BY_STATUS.new;
};

const inferInventoryChips = (s) => {
  if (Array.isArray(s.product_categories) && s.product_categories.length) {
    const map = { laptops: 'Laptops', desktops: 'Desktops', tablets: 'Tablets', servers: 'Servers', parts: 'Components', accessories: 'Components', monitors: 'Components' };
    const out = Array.from(new Set(s.product_categories.map((c) => map[(c || '').toLowerCase()] || (c || '')).filter(Boolean)));
    if (out.length > 0) return out.length >= 3 ? ['Mixed'] : out;
  }
  const haystack = [
    ...(s.brands_carried || []),
    ...(s.tags || []),
    s.supplier_type || '', s.notes || '',
  ].map((x) => (x || '').toLowerCase()).join(' ');
  const cats = new Set();
  if (/laptop|notebook|macbook|elitebook|thinkpad|latitude|probook|inspiron|chromebook/.test(haystack)) cats.add('Laptops');
  if (/desktop|workstation|optiplex|tower|imac|mac mini|aio|all.?in.?one/.test(haystack)) cats.add('Desktops');
  if (/tablet|ipad|surface pro|surface go/.test(haystack)) cats.add('Tablets');
  if (/server|rack|blade|poweredge|proliant|nas/.test(haystack)) cats.add('Servers');
  if (/component|part|ram|ssd|hdd|gpu|cpu|memory|disk|monitor|accessor/.test(haystack)) cats.add('Components');
  if (cats.size === 0) return ['Mixed'];
  if (cats.size >= 3) return ['Mixed'];
  return Array.from(cats);
};

const supplierLastContactIso = (s) =>
  s.last_contacted_at || s.last_email_at || s.last_outreach_at || s.updated_at || null;

const fmtRelativeDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days < 0) return d.toISOString().slice(0, 10);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  } catch { return '—'; }
};

const NEXT_ACTION_BY_STATUS = {
  new: 'Contact',
  research: 'Contact',
  contacted: 'Follow up',
  active: 'Request inventory',
  approved: 'Request inventory',
  preferred: 'Request inventory',
  waiting: 'Await reply',
  quoted: 'Await reply',
  rejected: '—',
  inactive: '—',
};
const supplierNextAction = (s) =>
  s.next_action || NEXT_ACTION_BY_STATUS[(s.status || '').toLowerCase()] || '—';

const EMPTY = {
  company_name: '', contact_name: '', email: '', whatsapp: '', phone: '',
  website: '', country: '', supplier_type: '', brands_carried: [],
  product_categories: [], tags: [], status: 'new', notes: '',
};

export const AdminSuppliers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [enrichmentFilter, setEnrichmentFilter] = useState('');
  const [emailVerifyFilter, setEmailVerifyFilter] = useState('');
  const [nextActionFilter, setNextActionFilter] = useState('');
  const [rankingFilter, setRankingFilter] = useState('');  // '' | '5' | '4' | '3' | '2' | '1'
  const [inventoryFilter, setInventoryFilter] = useState('');  // '' | Laptops | Desktops | ...
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [enrichingId, setEnrichingId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [verifyingEmailId, setVerifyingEmailId] = useState(null);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkVerifyingEmails, setBulkVerifyingEmails] = useState(false);
  const [sortBy, setSortBy] = useState('created_desc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);

  // Danger-reset modal state (uses DangerConfirmModal)
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Bulk Send-RFQ state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendOpen, setSendOpen] = useState(false);
  const [sendMode, setSendMode] = useState('new'); // 'new' | 'existing'
  const [draftCampaigns, setDraftCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [sendTemplates, setSendTemplates] = useState([]);
  const [sendTemplateId, setSendTemplateId] = useState('');
  const [sendCampaignName, setSendCampaignName] = useState('');
  const [sendProductText, setSendProductText] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [sendImmediately, setSendImmediately] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // Apply client-side sorting to the already-filtered list
  const sortedItems = useMemo(() => {
    // Frontend-only Next Action / Ranking / Inventory filters. All three use
    // the same helpers the table renders, so dropdowns and cells always agree.
    let filtered = items;
    if (nextActionFilter) {
      filtered = filtered.filter((s) => supplierNextAction(s) === nextActionFilter);
    }
    if (rankingFilter) {
      const target = parseInt(rankingFilter, 10);
      filtered = filtered.filter((s) => supplierRanking(s).stars === target);
    }
    if (inventoryFilter) {
      filtered = filtered.filter((s) => inferInventoryChips(s).includes(inventoryFilter));
    }
    const arr = [...filtered];
    const cmp = (a, b, key) => {
      const av = (a[key] || '').toString().toLowerCase();
      const bv = (b[key] || '').toString().toLowerCase();
      return av.localeCompare(bv);
    };
    switch (sortBy) {
      case 'company_asc':   arr.sort((a, b) => cmp(a, b, 'company_name')); break;
      case 'company_desc':  arr.sort((a, b) => cmp(b, a, 'company_name')); break;
      case 'country_asc':   arr.sort((a, b) => cmp(a, b, 'country')); break;
      case 'country_desc':  arr.sort((a, b) => cmp(b, a, 'country')); break;
      case 'status_asc':    arr.sort((a, b) => cmp(a, b, 'status')); break;
      case 'type_asc':      arr.sort((a, b) => cmp(a, b, 'supplier_type')); break;
      case 'updated_desc':  arr.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')); break;
      case 'last_contact_asc': {
        // Oldest dated contact first; suppliers with no last-contact date
        // sink to the bottom. We use a far-future ISO sentinel because
        // localeCompare's ordering of non-digit chars vs digits is
        // locale-dependent and not safe to rely on.
        const NO_DATE = '9999-12-31T23:59:59Z';
        arr.sort((a, b) => (supplierLastContactIso(a) || NO_DATE).localeCompare(supplierLastContactIso(b) || NO_DATE));
        break;
      }
      case 'created_desc':
      default:              arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
    return arr;
  }, [items, sortBy, nextActionFilter, rankingFilter, inventoryFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.supplier_type = typeFilter;
      if (tagFilter) params.tag = tagFilter;
      if (enrichmentFilter) params.enrichment_status = enrichmentFilter;
      if (emailVerifyFilter) params.email_verification_status = emailVerifyFilter;
      const { data } = await listSuppliers(params);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, typeFilter, tagFilter, enrichmentFilter, emailVerifyFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runEnrich = async (s) => {
    try {
      setEnrichingId(s.id);
      const { data } = await enrichSupplier(s.id);
      if (data.filled?.length) {
        toast.success(`Enriched: ${data.filled.join(', ')}`);
      } else if (data.error) {
        toast.error(`No contact found · ${data.error}`);
      } else {
        toast.info('Website fetched but no new contact data extracted.');
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enrichment failed');
    } finally {
      setEnrichingId(null);
    }
  };

  const runVerify = async (s) => {
    if (!window.confirm(`Mark ${s.company_name} as VERIFIED? This indicates a human reviewer has checked the contact data.`)) return;
    try {
      setVerifyingId(s.id);
      await verifySupplier(s.id);
      toast.success('Supplier verified');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifyingId(null);
    }
  };

  const runBulkEnrich = async () => {
    if (!window.confirm('Run enrichment on up to 10 suppliers missing any contact field? (run multiple times to process more)')) return;
    try {
      setBulkEnriching(true);
      const { data } = await bulkEnrichSuppliers();
      if (data.total === 0) {
        toast.info('No suppliers need enrichment.');
      } else {
        const remaining = data.remaining || 0;
        const failed = data.failed || 0;
        const parts = [
          `Processed ${data.total} — enriched ${data.enriched}, no contact ${data.no_contact_found}`,
        ];
        if (failed) parts.push(`failed ${failed}`);
        if (remaining) parts.push(`${remaining} still need enrichment — run again`);
        toast.success(parts.join(' · '));
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk enrichment failed');
    } finally {
      setBulkEnriching(false);
    }
  };

  const runVerifyEmail = async (s) => {
    try {
      setVerifyingEmailId(s.id);
      const { data } = await verifySupplierEmail(s.id);
      const t = data.status === 'valid_mx'
        ? toast.success
        : data.status === 'invalid'
          ? toast.error
          : toast.info;
      t(`${s.company_name}: ${data.status} · ${data.notes}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email verification failed');
    } finally {
      setVerifyingEmailId(null);
    }
  };

  const runMarkEmailVerified = async (s) => {
    const notes = window.prompt(
      `Mark ${s.company_name}'s email as MANUALLY VERIFIED?\n\nUse this only when you've confirmed deliverability outside the app (e.g. got a reply, phone confirmation).\n\nOptional note (e.g. "Confirmed by phone with rep on 2026-02-15"):`,
      ''
    );
    if (notes === null) return;
    try {
      setVerifyingEmailId(s.id);
      await markSupplierEmailVerified(s.id, notes);
      toast.success(`${s.company_name} marked verified`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark verified');
    } finally {
      setVerifyingEmailId(null);
    }
  };

  const runBulkVerifyEmails = async () => {
    if (!window.confirm('Verify (MX-record check) every supplier email that is not yet `valid_mx` or manually `verified`? Capped at 200 per call.')) return;
    try {
      setBulkVerifyingEmails(true);
      const { data } = await bulkVerifySupplierEmails();
      if (data.total === 0) {
        toast.info('No supplier emails to verify.');
      } else {
        const c = data.counts || {};
        toast.success(`Verified ${data.total} emails — valid_mx ${c.valid_mx || 0} · invalid ${c.invalid || 0} · unknown ${c.unknown || 0}`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk email verification failed');
    } finally {
      setBulkVerifyingEmails(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      company_name: s.company_name || '', contact_name: s.contact_name || '',
      email: s.email || '', whatsapp: s.whatsapp || '', phone: s.phone || '',
      website: s.website || '', country: s.country || '',
      supplier_type: s.supplier_type || '',
      brands_carried: s.brands_carried || [], product_categories: s.product_categories || [],
      tags: s.tags || [],
      status: s.status || 'new', notes: s.notes || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.supplier_type) delete payload.supplier_type;
      if (editing) {
        await updateSupplier(editing.id, payload);
        toast.success('Supplier updated');
      } else {
        await createSupplier(payload);
        toast.success('Supplier created');
      }
      setModalOpen(false); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete supplier "${s.company_name}"?`)) return;
    try {
      await deleteSupplier(s.id);
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error('Delete failed'); }
  };

  const toggleArrayValue = (field, val) => {
    setForm((f) => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const handleImportPreview = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const { data } = await supplierImportPreview(importFile, importOverwrite);
      setImportPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setImporting(false); }
  };

  const handleImportCommit = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const { data } = await supplierImportCommit(importFile, importOverwrite);
      const errCount = (data.errors || []).length;
      const invalidCount = data.invalid_type || 0;
      toast.success(`Created ${data.created} · Updated ${data.updated} · Skipped ${data.skipped} · Invalid Type ${invalidCount} · Errors ${errCount}`);
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportOverwrite(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleExportCsv = async () => {
    const token = localStorage.getItem('luxortek_admin_token');
    if (!token) {
      toast.error('Export failed: please sign in again.');
      return;
    }
    try {
      // Use a same-origin relative URL (matches axios baseURL='/api' everywhere
      // else in the app). Going through REACT_APP_BACKEND_URL would force a
      // cross-origin CORS preflight that Cloudflare/ingress drops in prod,
      // surfacing as 'TypeError: Failed to fetch' in the browser.
      const res = await fetch(exportSuppliersUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.detail) detail = typeof body.detail === 'string' ? body.detail : detail;
        } catch (_) { /* not JSON */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `suppliers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  // Supplier dataset reset handler (via DangerConfirmModal)
  const handleDangerReset = async ({ confirmation_phrase, acknowledged }) => {
    setResetting(true);
    try {
      const { data } = await dangerResetSuppliers(confirmation_phrase, acknowledged);
      toast.success(
        `Reset complete · suppliers ${data.suppliers_deleted} · quotes ${data.quotes_deleted} · email logs ${data.email_logs_deleted}`
      );
      setResetOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  // ── Bulk Send-RFQ ─────────────────────────────────────────────────
  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const allVisibleSelected = sortedItems.length > 0 && sortedItems.every((s) => selectedIds.has(s.id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) sortedItems.forEach((s) => n.delete(s.id));
      else sortedItems.forEach((s) => n.add(s.id));
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const openSendRfq = async () => {
    setSendOpen(true);
    setSendMode('new');
    setSelectedCampaignId('');
    setSendTemplateId('');
    setSendCampaignName('');
    setSendProductText('');
    setSendSubject('');
    setSendBody('');
    setSendImmediately(true);
    try {
      const [d, t] = await Promise.all([
        listRfqCampaigns({ status: 'draft' }),
        listRfqTemplates(),
      ]);
      setDraftCampaigns(d.data.items || []);
      setSendTemplates(t.data.items || []);
    } catch (e) {
      toast.error('Failed to load campaigns/templates');
    }
  };

  const applySendTemplate = (templateId) => {
    setSendTemplateId(templateId);
    if (!templateId) return;
    const tpl = sendTemplates.find((t) => t.id === templateId);
    if (!tpl) return;
    setSendSubject(tpl.email_subject || '');
    setSendBody(tpl.email_body || '');
    if (tpl.product_request_text) setSendProductText(tpl.product_request_text);
    toast.success(`Template applied: ${tpl.template_name}`);
  };

  const handlePreviewRfq = async () => {
    if (!(sendBody || '').trim()) { toast.error('Email body is empty'); return; }
    setPreviewing(true);
    try {
      const { data } = await previewRfq({
        email_subject: sendSubject,
        email_body: sendBody,
        product_request_text: sendProductText,
      });
      setPreviewData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const handleSendRfqSubmit = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('No suppliers selected'); return; }

    // Front-end validation: if body has the placeholder, product text must be present
    if (sendMode === 'new' && (sendBody || '').includes('[PRODUCT_REQUEST_TEXT]')
        && !(sendProductText || '').trim()) {
      toast.error('Product Request Text is empty but the email body contains [PRODUCT_REQUEST_TEXT]. Fill it in before sending.');
      return;
    }

    setSending(true);
    try {
      let campaignId;
      if (sendMode === 'existing') {
        if (!selectedCampaignId) { toast.error('Pick a draft campaign'); setSending(false); return; }
        const existing = draftCampaigns.find((c) => c.id === selectedCampaignId);
        const merged = Array.from(new Set([...(existing?.supplier_ids || []), ...ids]));
        await updateRfqCampaign(selectedCampaignId, { supplier_ids: merged });
        campaignId = selectedCampaignId;
      } else {
        if (!sendCampaignName.trim()) { toast.error('Campaign name required'); setSending(false); return; }
        const payload = {
          campaign_name: sendCampaignName.trim(),
          supplier_ids: ids,
          email_subject: sendSubject || '',
          email_body: sendBody || '',
          product_request_text: sendProductText || '',
        };
        const { data } = await createRfqCampaign(payload);
        campaignId = data.id;
      }

      if (sendImmediately) {
        const { data } = await sendRfqCampaign(campaignId, {});
        toast.success(`Sent ${data.sent}/${data.total} (failed: ${data.failed})`);
      } else {
        toast.success(`Campaign saved as draft with ${ids.length} supplier(s)`);
      }
      setSendOpen(false);
      clearSelection();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="px-6 py-4 text-white" data-testid="admin-suppliers">
      <Toaster theme="dark" />
      <div className="flex flex-col gap-1 mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Suppliers</h1>
        <p className="text-white/50 text-sm">Manage your sourcing/vendor database.</p>
      </div>

      {/* Filters + actions */}
      <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="suppliers-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by company, email, country, website…"
            className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-sm text-sm focus:outline-none focus:border-[#00D4FF]/50"
          />
        </div>
        <select
          data-testid="suppliers-ranking-filter"
          value={rankingFilter} onChange={(e) => setRankingFilter(e.target.value)}
          className="ipt-select"
          style={{ width: 170 }}
        >
          <option value="">All Rankings</option>
          <option value="5">Preferred / 5★</option>
          <option value="4">Approved / 4★</option>
          <option value="3">Qualified / 3★</option>
          <option value="2">Prospect / 2★</option>
          <option value="1">Research / 1★</option>
        </select>
        <select
          data-testid="suppliers-type-filter"
          value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="ipt-select"
          style={{ width: 140 }}
        >
          <option value="">All types</option>
          {SUPPLIER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          data-testid="suppliers-inventory-filter"
          value={inventoryFilter} onChange={(e) => setInventoryFilter(e.target.value)}
          className="ipt-select"
          style={{ width: 160 }}
        >
          <option value="">All Inventory</option>
          <option value="Laptops">Laptops</option>
          <option value="Desktops">Desktops</option>
          <option value="Tablets">Tablets</option>
          <option value="Servers">Servers</option>
          <option value="Components">Components</option>
          <option value="Mixed">Mixed</option>
        </select>
        <select
          data-testid="suppliers-next-action-filter"
          value={nextActionFilter} onChange={(e) => setNextActionFilter(e.target.value)}
          className="ipt-select"
          style={{ width: 170 }}
        >
          <option value="">All Actions</option>
          <option value="Contact">Contact</option>
          <option value="Follow up">Follow up</option>
          <option value="Request inventory">Request inventory</option>
          <option value="Await reply">Await reply</option>
        </select>
        <select
          data-testid="suppliers-sort-by"
          value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          className="ipt-select"
          style={{ width: 180 }}
        >
          <option value="created_desc">Recently added</option>
          <option value="updated_desc">Recently updated</option>
          <option value="last_contact_asc">Last Contact: Oldest first</option>
          <option value="company_asc">Company A–Z</option>
          <option value="company_desc">Company Z–A</option>
          <option value="country_asc">Country A–Z</option>
          <option value="country_desc">Country Z–A</option>
          <option value="status_asc">Status</option>
          <option value="type_asc">Type</option>
        </select>
        <button
          type="button"
          data-testid="suppliers-more-filters-btn"
          onClick={() => setShowMoreFilters((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2.5 border text-sm transition-colors ${showMoreFilters ? 'border-[#00D4FF]/40 text-[#00D4FF] bg-[#00D4FF]/5' : 'border-white/10 text-white/60 hover:bg-white/5'}`}
          title="Status / Tags / Enrichment / Email status filters"
        >
          {showMoreFilters ? 'Hide Filters' : 'More Filters'}
          {([statusFilter, tagFilter, enrichmentFilter, emailVerifyFilter].filter(Boolean).length > 0) && (
            <span className="px-1.5 py-px text-[10px] bg-[#00D4FF]/15 text-[#00D4FF] rounded-sm">
              {[statusFilter, tagFilter, enrichmentFilter, emailVerifyFilter].filter(Boolean).length}
            </span>
          )}
        </button>
        <button
          data-testid="suppliers-bulk-enrich-btn"
          onClick={runBulkEnrich}
          disabled={bulkEnriching}
          className="flex items-center gap-2 px-4 py-2.5 border border-amber-400/40 bg-amber-400/10 text-amber-200 text-sm hover:bg-amber-400/20 disabled:opacity-40"
          title="Scrape each missing-info supplier's website and fill in email / phone / WhatsApp / LinkedIn (capped 50/run)"
        >
          {bulkEnriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {bulkEnriching ? 'Enriching…' : 'Enrich Missing Contacts'}
        </button>
        <button
          data-testid="suppliers-bulk-verify-emails-btn"
          onClick={runBulkVerifyEmails}
          disabled={bulkVerifyingEmails}
          className="flex items-center gap-2 px-4 py-2.5 border border-cyan-400/40 bg-cyan-400/10 text-cyan-200 text-sm hover:bg-cyan-400/20 disabled:opacity-40"
          title="DNS MX-record check for every supplier email not yet valid_mx / manually verified (capped 200/run)"
        >
          {bulkVerifyingEmails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {bulkVerifyingEmails ? 'Verifying…' : 'Verify Supplier Emails'}
        </button>
        <button
          data-testid="suppliers-export-btn"
          onClick={handleExportCsv}
          className="flex items-center gap-2 px-4 py-2.5 border border-white/10 text-sm hover:bg-white/5"
          title="Download all suppliers as CSV (edit offline, then re-import)"
        >
          <Download className="w-4 h-4" /> Export
        </button>
        <button
          data-testid="suppliers-import-btn"
          onClick={() => { setImportOpen(true); setImportFile(null); setImportPreview(null); setImportOverwrite(false); }}
          className="flex items-center gap-2 px-4 py-2.5 border border-white/10 text-sm hover:bg-white/5"
        >
          <Upload className="w-4 h-4" /> Import
        </button>
        {/* Danger reset button */}
        <button
          data-testid="suppliers-danger-reset-btn"
          onClick={() => setResetOpen(true)}
          className="flex items-center gap-2 px-3 py-2.5 border border-red-500/40 text-red-300 text-sm hover:bg-red-500/10"
          title="Reset: wipe all suppliers + quotes + email logs"
        >
          <AlertTriangle className="w-4 h-4" /> Reset
        </button>
        <button
          data-testid="suppliers-create-btn"
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#00D4FF] text-black font-semibold text-sm hover:bg-[#00D4FF]/90"
        >
          <Plus className="w-4 h-4" /> Add Supplier
        </button>
      </div>

      {/* "More Filters" collapsible — low-priority filters live here. */}
      {showMoreFilters && (
        <div
          className="flex flex-wrap items-center gap-3 mb-5 px-3 py-2.5 bg-white/[0.02] border border-white/5 -mt-2"
          data-testid="suppliers-more-filters-row"
        >
          <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">More:</span>
          <select
            data-testid="suppliers-status-filter"
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="ipt-select" style={{ width: 140 }}
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            data-testid="suppliers-tag-filter"
            value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
            className="ipt-select" style={{ width: 160 }}
          >
            <option value="">All tags</option>
            {TAG_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            data-testid="suppliers-enrichment-filter"
            value={enrichmentFilter} onChange={(e) => setEnrichmentFilter(e.target.value)}
            className="ipt-select" style={{ width: 200 }}
          >
            <option value="">All enrichment</option>
            {ENRICHMENT_STATUSES.map((e) => <option key={e} value={e}>{e.replaceAll('_', ' ')}</option>)}
          </select>
          <select
            data-testid="suppliers-email-verify-filter"
            value={emailVerifyFilter} onChange={(e) => setEmailVerifyFilter(e.target.value)}
            className="ipt-select" style={{ width: 200 }}
          >
            <option value="">All email status</option>
            {EMAIL_VERIFICATION_STATUSES.map((e) => <option key={e} value={e}>email · {EMAIL_VERIFICATION_LABEL[e]}</option>)}
          </select>
        </div>
      )}

      {/* Bulk action toolbar — shows when at least 1 supplier selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 bg-[#00D4FF]/10 border border-[#00D4FF]/30" data-testid="suppliers-bulk-toolbar">
          <div className="text-sm text-[#00D4FF]">
            <span className="font-semibold" data-testid="suppliers-selected-count">{selectedIds.size}</span> supplier{selectedIds.size === 1 ? '' : 's'} selected
          </div>
          <div className="flex gap-2">
            <button onClick={clearSelection} data-testid="suppliers-clear-selection" className="px-3 py-1.5 text-sm text-white/60 hover:text-white">Clear</button>
            <button onClick={openSendRfq} data-testid="suppliers-bulk-send-rfq" className="flex items-center gap-2 px-3 py-1.5 bg-[#00D4FF] text-black font-semibold text-sm hover:bg-[#00D4FF]/90">
              <Send className="w-4 h-4" /> Send RFQ
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-white/10 overflow-x-auto" data-testid="suppliers-table">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-[#0A0A0A] text-white/60 text-[10px] uppercase tracking-wider shadow-[0_1px_0_rgba(255,255,255,0.05)]">
            <tr>
              <th className="px-2 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  data-testid="suppliers-select-all"
                  aria-label="Select all visible"
                />
              </th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Website</th>
              <th className="text-left px-3 py-2">Ranking</th>
              <th className="text-left px-3 py-2">Inventory</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Last Contact</th>
              <th className="text-left px-3 py-2">Next Action</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="11" className="px-4 py-10 text-center text-white/40">Loading…</td></tr>
            )}
            {!loading && sortedItems.length === 0 && (
              <tr><td colSpan="11" className="px-4 py-10 text-center text-white/40">No suppliers yet. Click "Add Supplier" to create one.</td></tr>
            )}
            {!loading && sortedItems.map((s) => {
              const ranking = supplierRanking(s);
              const inv = inferInventoryChips(s);
              const lastIso = supplierLastContactIso(s);
              const nextAction = supplierNextAction(s);
              const enrichBadge = s.enrichment_status ? s.enrichment_status.replaceAll('_', ' ') : '';
              const emailBadge = s.email_verification_status ? (EMAIL_VERIFICATION_LABEL[s.email_verification_status] || s.email_verification_status) : '';
              const statusTitle = [
                enrichBadge && `Enrichment: ${enrichBadge}`,
                emailBadge && `Email: ${emailBadge}`,
                s.country && `Country: ${s.country}`,
                (s.brands_carried || []).length && `Brands: ${(s.brands_carried || []).join(', ')}`,
              ].filter(Boolean).join('\n');
              return (
                <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleRowSelected(s.id)}
                      data-testid={`supplier-checkbox-${s.id}`}
                      aria-label={`Select ${s.company_name}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-white text-[13px]" title={statusTitle}>{s.company_name}</div>
                    {s.contact_name && <div className="text-[10px] text-white/40">{s.contact_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-white/60">{s.supplier_type || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-0.5 text-[11px]">
                      {s.email && <span className="flex items-center gap-1 text-white/70"><Mail className="w-3 h-3" />{s.email}</span>}
                      {s.whatsapp && <span className="flex items-center gap-1 text-white/70"><MessageCircle className="w-3 h-3" />{s.whatsapp}</span>}
                      {s.phone && <span className="flex items-center gap-1 text-white/70"><Phone className="w-3 h-3" />{s.phone}</span>}
                      {!(s.email || s.whatsapp || s.phone) && <span className="text-white/30">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {s.website ? (
                      <a
                        href={s.website.startsWith('http') ? s.website : `https://${s.website}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#00D4FF] hover:underline text-[11px]"
                        title={s.website}
                        data-testid={`supplier-website-${s.id}`}
                      ><Globe className="w-3 h-3" />Website</a>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[#F59E0B] tracking-wider text-[11px]" title={`${ranking.label} (${ranking.stars}/5)`}>
                      {'★'.repeat(ranking.stars)}<span className="text-white/15">{'★'.repeat(5 - ranking.stars)}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-0.5">
                      {inv.map((c) => (
                        <span key={c} className="px-1.5 py-px bg-white/5 border border-white/10 text-[10px] text-white/70">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 text-[10px] ${STATUS_COLOR[s.status] || STATUS_COLOR.new}`}
                      title={statusTitle || undefined}
                    >{s.status}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-white/60" title={lastIso || ''}>
                    {fmtRelativeDate(lastIso)}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-white/70">{nextAction}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {needsEmailVerify(s) && (
                      <button
                        onClick={() => runVerifyEmail(s)}
                        disabled={verifyingEmailId === s.id}
                        data-testid={`supplier-verify-email-${s.id}`}
                        title="DNS MX-record check for this supplier's email"
                        className="p-1 text-cyan-300 hover:text-cyan-200 disabled:opacity-40"
                      >
                        {verifyingEmailId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {(s.email || '').trim() && s.email_verification_status !== 'verified' && (
                      <button
                        onClick={() => runMarkEmailVerified(s)}
                        disabled={verifyingEmailId === s.id}
                        data-testid={`supplier-mark-email-verified-${s.id}`}
                        title="Manual override — confirm email deliverability outside the app"
                        className="p-1 text-emerald-300/70 hover:text-emerald-200 disabled:opacity-40"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {supplierNeedsEnrichment(s) && (
                      <button
                        onClick={() => runEnrich(s)}
                        disabled={enrichingId === s.id}
                        data-testid={`supplier-enrich-${s.id}`}
                        title="Scrape this supplier's website to fill missing email / phone / WhatsApp / LinkedIn"
                        className="p-1 text-amber-300 hover:text-amber-200 disabled:opacity-40"
                      >
                        {enrichingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {s.enrichment_status === 'enriched' && (
                      <button
                        onClick={() => runVerify(s)}
                        disabled={verifyingId === s.id}
                        data-testid={`supplier-verify-${s.id}`}
                        title="Mark contact data as human-verified"
                        className="p-1 text-emerald-300 hover:text-emerald-200 disabled:opacity-40"
                      >
                        {verifyingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.id}`} className="p-1 text-white/60 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(s)} data-testid={`supplier-delete-${s.id}`} className="p-1 text-white/60 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setModalOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 max-h-[90vh] overflow-y-auto"
            data-testid="supplier-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
              <h2 className="text-lg font-semibold">{editing ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Company name *">
                <input data-testid="supplier-field-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="ipt" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Contact name"><input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="ipt" /></Field>
                <Field label="Country"><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="ipt" /></Field>
                <Field label="Email"><input data-testid="supplier-field-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ipt" /></Field>
                <Field label="WhatsApp"><input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="ipt" /></Field>
                <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ipt" /></Field>
                <Field label="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="ipt" /></Field>
                <Field label="Supplier type">
                  <select value={form.supplier_type} onChange={(e) => setForm({ ...form, supplier_type: e.target.value })} className="ipt-select">
                    <option value="">—</option>
                    {SUPPLIER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="ipt-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Brands carried">
                <div className="flex flex-wrap gap-2">
                  {BRAND_OPTIONS.map((b) => (
                    <button type="button" key={b} onClick={() => toggleArrayValue('brands_carried', b)}
                      className={`px-3 py-1.5 text-xs border ${form.brands_carried.includes(b) ? 'bg-[#00D4FF]/20 border-[#00D4FF]/50 text-[#00D4FF]' : 'border-white/10 text-white/60'}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Product categories">
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map((c) => (
                    <button type="button" key={c} onClick={() => toggleArrayValue('product_categories', c)}
                      className={`px-3 py-1.5 text-xs border ${form.product_categories.includes(c) ? 'bg-[#00D4FF]/20 border-[#00D4FF]/50 text-[#00D4FF]' : 'border-white/10 text-white/60'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Tags">
                <div className="flex flex-wrap gap-2" data-testid="supplier-tag-picker">
                  {TAG_OPTIONS.map((t) => (
                    <button type="button" key={t} onClick={() => toggleArrayValue('tags', t)}
                      className={`px-3 py-1.5 text-xs border ${form.tags.includes(t) ? 'bg-emerald-400/20 border-emerald-400/50 text-emerald-200' : 'border-white/10 text-white/60'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Notes">
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="ipt resize-none" />
              </Field>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5">
              <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button data-testid="supplier-save-btn" type="submit" disabled={saving} className="px-4 py-2 bg-[#00D4FF] text-black font-semibold text-sm disabled:opacity-50">{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk Send RFQ modal */}
      {sendOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSendOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 max-h-[92vh] overflow-y-auto" data-testid="suppliers-send-rfq-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
              <h2 className="text-lg font-semibold">Send RFQ — {selectedIds.size} supplier{selectedIds.size === 1 ? '' : 's'}</h2>
              <button onClick={() => setSendOpen(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Mode toggle */}
              <div className="flex border border-white/10 overflow-hidden">
                <button onClick={() => setSendMode('new')} data-testid="send-mode-new"
                        className={`flex-1 px-3 py-2 text-sm ${sendMode === 'new' ? 'bg-[#00D4FF] text-black font-semibold' : 'text-white/60 hover:bg-white/5'}`}>
                  New campaign
                </button>
                <button onClick={() => setSendMode('existing')} data-testid="send-mode-existing"
                        className={`flex-1 px-3 py-2 text-sm border-l border-white/10 ${sendMode === 'existing' ? 'bg-[#00D4FF] text-black font-semibold' : 'text-white/60 hover:bg-white/5'}`}>
                  Add to draft
                </button>
              </div>

              {sendMode === 'new' && (
                <div className="space-y-4" data-testid="send-mode-new-fields">
                  <Field label="Campaign name *">
                    <input data-testid="send-new-name" value={sendCampaignName}
                           onChange={(e) => setSendCampaignName(e.target.value)} className="ipt"
                           placeholder="e.g. UAE Laptops Q1 Sourcing" />
                  </Field>
                  <Field label="Template (pre-fills Subject, Body, Product text — all editable below)">
                    <select data-testid="send-new-template" value={sendTemplateId}
                            onChange={(e) => applySendTemplate(e.target.value)} className="ipt-select">
                      <option value="">— Choose a template (optional) —</option>
                      {sendTemplates.map((t) => (
                        <option key={t.id} value={t.id}>{t.template_name}{t.destination_country ? ` (${t.destination_country})` : ''}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Email subject">
                    <input data-testid="send-new-subject" value={sendSubject}
                           onChange={(e) => setSendSubject(e.target.value)} className="ipt"
                           placeholder="RFQ – ..." />
                  </Field>
                  <Field label="Email body (use [PRODUCT_REQUEST_TEXT] where the product list should be inserted)">
                    <textarea data-testid="send-new-body" value={sendBody} rows={10}
                              onChange={(e) => setSendBody(e.target.value)} className="ipt resize-none font-mono text-xs"
                              placeholder="Hello,&#10;&#10;We are sourcing ...&#10;&#10;[PRODUCT_REQUEST_TEXT]&#10;&#10;Best regards,&#10;Luxor Tek" />
                  </Field>
                  <Field label="Product request text (replaces [PRODUCT_REQUEST_TEXT])">
                    <textarea data-testid="send-new-product" value={sendProductText} rows={6}
                              onChange={(e) => setSendProductText(e.target.value)} className="ipt resize-none"
                              placeholder="e.g.&#10;HP EliteBook 840 G8 — Qty 100, Grade A, 16GB / 512GB SSD, Battery 80%+&#10;Dell Latitude 5420 — Qty 50, Grade A-, 8GB / 256GB SSD, FOB price requested" />
                  </Field>
                </div>
              )}

              {sendMode === 'existing' && (
                <div className="space-y-4" data-testid="send-mode-existing-fields">
                  <Field label="Draft campaign *">
                    <select data-testid="send-existing-campaign" value={selectedCampaignId}
                            onChange={(e) => setSelectedCampaignId(e.target.value)} className="ipt-select">
                      <option value="">— Pick a draft —</option>
                      {draftCampaigns.map((c) => (
                        <option key={c.id} value={c.id}>{c.campaign_name} ({(c.supplier_ids || []).length} suppliers)</option>
                      ))}
                    </select>
                  </Field>
                  {draftCampaigns.length === 0 && (
                    <div className="text-xs text-white/40">No draft campaigns exist. Switch to "New campaign" instead.</div>
                  )}
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={sendImmediately}
                       onChange={(e) => setSendImmediately(e.target.checked)}
                       data-testid="send-immediately-checkbox" />
                Send emails immediately
              </label>
              {!sendImmediately && (
                <div className="text-xs text-white/40">The campaign will be saved as a draft. You can send it later from the RFQ Campaigns page.</div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5 sticky bottom-0 bg-[#0A0A0A]">
              <button onClick={() => setSendOpen(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              {sendMode === 'new' && (
                <button onClick={handlePreviewRfq} disabled={previewing} data-testid="send-preview-btn"
                        className="px-4 py-2 border border-[#00D4FF]/40 text-[#00D4FF] text-sm hover:bg-[#00D4FF]/10 disabled:opacity-50">
                  {previewing ? 'Rendering…' : 'Preview email'}
                </button>
              )}
              <button onClick={handleSendRfqSubmit} disabled={sending} data-testid="send-rfq-submit-btn"
                      className="px-4 py-2 bg-[#00D4FF] text-black font-semibold text-sm disabled:opacity-50">
                {sending ? 'Working…' : sendImmediately ? 'Send RFQ' : 'Save as draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email preview modal (renders on top of Send modal) */}
      {previewData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewData(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-[#00D4FF]/40 max-h-[90vh] overflow-y-auto" data-testid="rfq-preview-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
              <h3 className="text-lg font-semibold">Email preview</h3>
              <button onClick={() => setPreviewData(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {previewData.missing_product_request_text && (
                <div className="px-3 py-2 bg-red-400/15 border border-red-400/40 text-red-200 text-sm" data-testid="preview-missing-warning">
                  ⚠️ Product Request Text is empty but the body contains [PRODUCT_REQUEST_TEXT]. Fill it in before sending.
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Subject</div>
                <div className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="preview-subject">{previewData.subject || '(empty)'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Rendered body (what suppliers will receive)</div>
                <pre className="bg-white text-black px-4 py-3 text-sm whitespace-pre-wrap font-sans border border-white/10 max-h-[50vh] overflow-y-auto" data-testid="preview-body">{previewData.rendered_body}</pre>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5 sticky bottom-0 bg-[#0A0A0A]">
              <button onClick={() => setPreviewData(null)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setImportOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 max-h-[90vh] overflow-y-auto" data-testid="supplier-import-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
              <h2 className="text-lg font-semibold">Import Suppliers</h2>
              <button onClick={() => setImportOpen(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-xs text-white/50">
                Accepts CSV or XLSX. Required column: <code>company_name</code>. Match priority: <code>supplier_id</code> → <code>email</code> → <code>company_name + website</code>. Importing never overwrites <code>enrichment_status</code> or <code>email_verification_status</code>.
              </div>
              <input
                data-testid="supplier-import-file"
                type="file" accept=".csv,.xlsx,.xls"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }}
                className="block text-sm text-white/80 file:mr-3 file:px-3 file:py-1.5 file:bg-white/10 file:border-0 file:text-white file:text-xs file:cursor-pointer cursor-pointer"
              />
              <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" checked={importOverwrite}
                  onChange={(e) => { setImportOverwrite(e.target.checked); setImportPreview(null); }}
                  data-testid="supplier-import-overwrite" />
                Overwrite existing values <span className="text-white/40">(default fills blank fields only)</span>
              </label>
              <div className="flex items-center gap-3">
                <button onClick={handleImportPreview} disabled={!importFile || importing} data-testid="supplier-import-preview-btn"
                        className="px-4 py-1.5 border border-white/10 text-sm hover:bg-white/5 disabled:opacity-50">
                  {importing ? 'Working…' : 'Preview'}
                </button>
              </div>

              {importPreview && (
                <div className="border border-white/10 p-4 space-y-3" data-testid="supplier-import-preview">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <Stat icon={CheckCircle2} color="emerald-300" label="New" value={importPreview.new_count} testId="preview-new-count" />
                    <Stat icon={CheckCircle2} color="blue-200" label="Updates" value={importPreview.update_count} testId="preview-update-count" />
                    <Stat icon={CheckCircle2} color="white/60" label="Skipped" value={importPreview.skipped_count || 0} testId="preview-skipped-count" />
                    <Stat icon={AlertCircle} color="amber-300" label="Duplicates" value={importPreview.duplicate_count || 0} testId="preview-duplicate-count" />
                    <Stat icon={AlertTriangle} color="amber-300" label="Invalid Type" value={importPreview.invalid_type_count || 0} testId="preview-invalid-type-count" />
                    <Stat icon={CheckCircle2} color="white/60" label="Total rows" value={importPreview.valid_rows} />
                    <Stat icon={AlertCircle} color="red-300" label="Errors" value={importPreview.error_count} />
                  </div>
                  {importPreview.errors?.length > 0 && (
                    <div className="text-xs text-red-300/80 max-h-24 overflow-y-auto">
                      {importPreview.errors.map((e, i) => <div key={i}>· {e}</div>)}
                    </div>
                  )}
                  {importPreview.preview_new?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40 mb-1">First {importPreview.preview_new.length} new</div>
                      <div className="max-h-32 overflow-y-auto text-xs text-white/70">
                        {importPreview.preview_new.map((r, i) => (
                          <div key={i} className="border-b border-white/5 py-1">{r.company_name} · {r.email || 'no email'} · {r.country || '—'}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  {importPreview.preview_updates?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Would update</div>
                      <div className="max-h-32 overflow-y-auto text-xs text-white/70">
                        {importPreview.preview_updates.map((r, i) => (
                          <div key={i} className="border-b border-white/5 py-1">
                            {r.new.company_name} → existing: {r.existing}
                            <span className="text-white/40"> · matched by {(r.matched_by || []).join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {importPreview.preview_skipped?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Will be skipped</div>
                      <div className="max-h-32 overflow-y-auto text-xs text-white/60" data-testid="preview-skipped-list">
                        {importPreview.preview_skipped.map((r, i) => (
                          <div key={i} className="border-b border-white/5 py-1">
                            {r.existing_company} <span className="text-white/40">· {r.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {importPreview.preview_duplicates?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Possible duplicates — resolve manually</div>
                      <div className="max-h-32 overflow-y-auto text-xs text-amber-200/80" data-testid="preview-duplicates-list">
                        {importPreview.preview_duplicates.map((r, i) => (
                          <div key={i} className="border-b border-white/5 py-1">
                            {r.row.company_name || r.row.email || '(row)'} matches {r.matched_companies.join(', ')}
                            <span className="text-white/40"> · via {(r.matched_by || []).join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5">
              <button onClick={() => setImportOpen(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button onClick={handleImportCommit} disabled={!importFile || importing} data-testid="supplier-import-commit-btn"
                      className="px-4 py-2 bg-[#00D4FF] text-black font-semibold text-sm disabled:opacity-50">
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Danger Reset Suppliers modal */}
      <DangerConfirmModal
        open={resetOpen}
        onClose={() => !resetting && setResetOpen(false)}
        onConfirm={handleDangerReset}
        title="Reset All Supplier Data"
        description="This will permanently delete all suppliers, supplier quotes, and RFQ email logs. RFQ campaigns, contacts, leads, buyers, orders, products, sourcing requests, and marketing data will not be touched. This cannot be undone."
        confirmPhrase="RESET ALL SUPPLIERS"
        loading={resetting}
      />

      <style>{`
        .ipt{width:100%;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);padding:0.55rem 0.75rem;font-size:0.875rem;color:white;outline:none}
        .ipt:focus{border-color:rgba(0,212,255,0.5)}
        .ipt-select{
          width:100%;
          background:#0A0A0A;
          color:white;
          border:1px solid rgba(255,255,255,0.15);
          padding:0.55rem 0.75rem;
          font-size:0.875rem;
          outline:none;
          color-scheme: dark;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3e%3cpath d='M1 1L5 5L9 1' stroke='%2300D4FF' stroke-width='1.5' stroke-linecap='round'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          padding-right: 2rem;
          cursor: pointer;
        }
        .ipt-select:focus{border-color:rgba(0,212,255,0.5)}
        .ipt-select:hover{border-color:rgba(0,212,255,0.3)}
        .ipt-select option{background:#0A0A0A;color:white;padding:8px}
        .ipt-select option:hover{background:#00D4FF;color:black}
        .ipt-select option:checked{background:#00D4FF;color:black;font-weight:600}
      `}</style>
    </div>
  );
};

const Stat = ({ icon: Icon, color, label, value, testId }) => (
  <div className={`flex items-center gap-2 text-${color}`} data-testid={testId}>
    <Icon className="w-4 h-4" />
    <span className="font-semibold">{value}</span>
    <span className="text-white/40 text-xs">{label}</span>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-white/60 mb-1 block">{label}</span>
    {children}
  </label>
);
